/**
 * HudPill — per-player HUD row: rounded background, color tag, name,
 * stretchy middle slot (progress bar / dots / custom), right-aligned metric.
 *
 * Used by horse-dash (progress bar + combo readout), puff-up (size bar +
 * combo), tap-tug (rope-pos bar + combo), fruit-grab (score dots).
 * Each was hand-rolled to ~60 lines per game; this consolidates them.
 *
 * The middle slot is intentionally a callback so games can render whatever
 * shape they need (progress, size, dots, sparkline) without HudPill
 * having to know about every variant. Pass `middleSlot: null` for no middle.
 *
 * Usage:
 *   import { HudPill } from '@platform/sdk';
 *
 *   start() {
 *     const W = this.scale.width;
 *     this._hud = {};
 *     this.players.forEach((p, i) => {
 *       this._hud[p.id] = new HudPill({
 *         scene: this,
 *         x: 12, y: 12 + i * 44,
 *         width: W - 24,
 *         color: PLAYER_COLORS[i],
 *         name: p.name,
 *         metric: 'x0',
 *         middleSlot: (pill) => pill.makeProgressBar()  // see below
 *       });
 *     });
 *   }
 *
 *   _renderHud() {
 *     for (const id of Object.keys(this._hud)) {
 *       const pill = this._hud[id];
 *       const player = this.state.players[id];
 *       pill.setProgress(player.progress);     // updates the bar created in middleSlot
 *       if (player.combo !== pill._lastCombo) {
 *         pill._lastCombo = player.combo;
 *         pill.setMetric('x' + player.combo, tier.color);
 *         pill.pulseMetric();
 *       }
 *     }
 *   }
 *
 * Each HudPill exposes `pill.container` (the Phaser Container) for layout,
 * `pill.makeProgressBar()` for the most common middle slot, and helpers
 * for the slot you draw yourself.
 */
const PILL_HEIGHT = 34;
const PILL_RADIUS = 14;
const TAG_SIZE    = 22;
const NAME_INSET  = 34;
const METRIC_INSET = 8;
const MIDDLE_LEFT_PAD  = 100;
const MIDDLE_RIGHT_PAD = 60;

export class HudPill {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {number} opts.x
   * @param {number} opts.y
   * @param {number} opts.width
   * @param {number} opts.color           player tint (24-bit hex)
   * @param {string} opts.name
   * @param {string} [opts.metric='']     initial right-side metric text
   * @param {number} [opts.height=34]
   * @param {(pill: HudPill) => void} [opts.middleSlot]  callback that builds
   *        whatever sits in the middle area. Inside the callback, use
   *        `pill.middleArea` ({x, y, width, height} relative to pill origin)
   *        to lay out your bars/dots. Or call `pill.makeProgressBar()` for
   *        the standard bar.
   * @param {number} [opts.maxNameChars=8]
   */
  constructor(opts) {
    const scene = opts.scene;
    this.scene = scene;
    this.color = opts.color != null ? opts.color : 0xffffff;
    this.height = opts.height || PILL_HEIGHT;
    this.width = opts.width;

    const w = this.width;
    const h = this.height;
    const tagY = (h - TAG_SIZE) / 2;

    this.container = scene.add.container(opts.x, opts.y);

    // Background pill.
    const bg = scene.add.graphics();
    bg.fillStyle(0x000000, 0.45);
    bg.fillRoundedRect(0, 0, w, h, PILL_RADIUS);
    bg.lineStyle(2, this.color, 0.95);
    bg.strokeRoundedRect(0, 0, w, h, PILL_RADIUS);
    this.container.add(bg);
    this.bg = bg;

    // Color tag (left).
    const tag = scene.add.graphics();
    tag.fillStyle(this.color, 1);
    tag.fillRoundedRect(6, tagY, TAG_SIZE, TAG_SIZE, TAG_SIZE / 2);
    tag.lineStyle(2, 0xffffff, 0.9);
    tag.strokeRoundedRect(6, tagY, TAG_SIZE, TAG_SIZE, TAG_SIZE / 2);
    this.container.add(tag);
    this.tag = tag;

    // Name text.
    const maxName = opts.maxNameChars || 8;
    this.nameText = scene.add.text(NAME_INSET, h / 2, String(opts.name || '').slice(0, maxName), {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    this.container.add(this.nameText);

    // Right-side metric text.
    this.metricText = scene.add.text(w - METRIC_INSET, h / 2, opts.metric || '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(1, 0.5);
    this.container.add(this.metricText);

    // Middle slot dimensions — exposed for the callback.
    this.middleArea = {
      x: MIDDLE_LEFT_PAD,
      y: 0,
      width: Math.max(20, w - MIDDLE_LEFT_PAD - MIDDLE_RIGHT_PAD),
      height: h
    };

    // Slot construction (callback owns the middle).
    if (typeof opts.middleSlot === 'function') {
      opts.middleSlot(this);
    }
  }

  /**
   * Build a standard progress bar in the middle slot. Call this from inside
   * the `middleSlot` callback (or after construction). The bar exposes
   * `setProgress(0..1)` via `HudPill.setProgress`.
   * @param {object} [opts]
   * @param {number} [opts.barHeight=12]
   * @param {number} [opts.trackColor=0xffffff]
   * @param {number} [opts.trackAlpha=0.18]
   * @param {boolean} [opts.gloss=true]   draw a 2px highlight along the top
   */
  makeProgressBar(opts = {}) {
    const barH = opts.barHeight || 12;
    const barY = (this.height - barH) / 2;
    const left = this.middleArea.x;
    const width = this.middleArea.width;
    const trackColor = opts.trackColor != null ? opts.trackColor : 0xffffff;
    const trackAlpha = opts.trackAlpha != null ? opts.trackAlpha : 0.18;

    const track = this.scene.add.graphics();
    track.fillStyle(trackColor, trackAlpha);
    track.fillRoundedRect(left, barY, width, barH, 6);
    this.container.add(track);

    const fill = this.scene.add.graphics();
    this.container.add(fill);

    this._bar = {
      track, fill, left, width, barH, barY,
      gloss: opts.gloss !== false,
      progress: 0
    };
  }

  /**
   * Update the progress bar fill (0..1). No-op if `makeProgressBar` wasn't called.
   */
  setProgress(p) {
    const bar = this._bar;
    if (!bar) return;
    p = Math.max(0, Math.min(1, p));
    bar.progress = p;
    const fillW = bar.width * p;
    bar.fill.clear();
    bar.fill.fillStyle(this.color, 1);
    bar.fill.fillRoundedRect(bar.left, bar.barY, Math.max(0, fillW), bar.barH, 6);
    if (bar.gloss) {
      bar.fill.fillStyle(0xffffff, 0.35);
      bar.fill.fillRect(bar.left, bar.barY + 2, Math.max(0, fillW), 2);
    }
  }

  /** Update the player's display name. */
  setName(name) {
    if (this.nameText) this.nameText.setText(String(name || ''));
  }

  /**
   * Update the right-side metric text and (optionally) recolor it.
   * @param {string} text
   * @param {number} [color]   24-bit hex; omit to keep current color
   */
  setMetric(text, color) {
    if (!this.metricText) return;
    this.metricText.setText(String(text));
    if (color != null) {
      this.metricText.setColor('#' + color.toString(16).padStart(6, '0'));
    }
  }

  /**
   * Pop the metric text — same scale tween every game uses when the value
   * changes (1.4 → 1, Back.out, 220ms).
   */
  pulseMetric() {
    if (!this.metricText) return;
    this.scene.tweens.killTweensOf(this.metricText);
    this.metricText.setScale(1.4);
    this.scene.tweens.add({
      targets: this.metricText,
      scale: 1,
      duration: 220,
      ease: 'Back.out'
    });
  }

  /**
   * Pop the entire pill — used to indicate "this player just scored".
   * Mirrors `HudRowAnimator.pulse` but operates on `this.container` so
   * the whole row scales.
   */
  pulse(positive = true) {
    this.scene.tweens.killTweensOf(this.container);
    this.scene.tweens.add({
      targets: this.container,
      scale: { from: positive ? 1.1 : 0.92, to: 1 },
      duration: 260,
      ease: 'Back.out'
    });
  }

  /** Tear down. */
  destroy() {
    if (this.scene) {
      this.scene.tweens.killTweensOf(this.container);
      this.scene.tweens.killTweensOf(this.metricText);
    }
    if (this.container) this.container.destroy();
    this.container = null;
    this.bg = null;
    this.tag = null;
    this.nameText = null;
    this.metricText = null;
    this._bar = null;
    this.scene = null;
  }
}
